import { call, put, select, takeLatest } from '@redux-saga/core/effects';
import * as types from '../../actions/shopList/shopListActionTypes';
import * as actions from '../../actions/shopList/shopListActions';
import { ShopListState } from '../../reducers/shopList/shopListReducer';
import reservationService from '../../services/reservationService';

// 店舗リスト取得
function* fetchShopsSaga(): Generator<unknown> {
  try {
    const state = (yield select()) as { shopList: ShopListState };
    const { searchParams, filters, searchMode } = state.shopList;

    const memberId = sessionStorage.getItem('memberID');
    const shopId = sessionStorage.getItem('shopId');

    const requestParams: any = {
      is_search_nearby: searchMode === 'map',
      member_id: memberId,
      club_id: shopId,
    };

    if (searchMode === 'keyword') {
      if (searchParams.area) {
        requestParams.region_code = parseInt(searchParams.area, 10);
      }
      if (searchParams.keyword) {
        requestParams.club_name_or_address = searchParams.keyword;
      }
    }

    const allTags = [...filters.facilities, ...filters.services].map((id) => parseInt(id, 10));
    if (allTags.length > 0) {
      requestParams.tag_list = allTags;
    }

    const response: any = yield call(reservationService().searchClubs, requestParams);

    if (response.data.result === 0) {
      const clubList = response.data.club_list || [];
      const shops = clubList.map((club: any) => ({
        id: club.club_id.toString(),
        name: club.club_name,
        image: club.ClubPicture || '/assets/img/map-pin-image@2x.png',
        businessHours: club.open_date,
        phone: club.telephone,
        address: `${club.post_id ? `〒${club.post_id}` : ''} ${club.address}${
          club.addr_buildname ? ` ${club.addr_buildname}` : ''
        }`,
        annexFlag: club.annex_flag,
        annexAddress:
          club.annex_flag &&
          (club.annex_post_id ? `〒${club.annex_post_id}` : '') +
            (club.annex_address ? ` ${club.annex_address}` : '') +
            (club.annex_addr_buildname ? ` ${club.annex_addr_buildname}` : ''),
        annexOpenDate: club.annex_flag && club.annex_open_date,
        features: (club.tag_list || []).map((tag: any) => ({
          id: tag.tag_id.toString(),
          name: tag.tag_name,
          icon: tag.thumbnail_image || '/assets/img/default-icon',
          category: 'facility' as const,
        })),
        isBookmarked: club.is_favorite,
        latitude: parseFloat(club.latitude),
        longitude: parseFloat(club.longitude),
        annexLatitude: club.annex_flag && parseFloat(club.annex_latitude),
        annexLongitude: club.annex_flag && parseFloat(club.annex_longitude),
        annexOpenDay: club.AnnexOpenDay,
        annexClosedDay: club.AnnexClosedDay,
        preOpenDay: club.pre_open_day,
      }));

      yield put(actions.fetchShopsSuccess(shops, response.data.total || 0));
    } else {
      throw new Error('API returned error result');
    }
  } catch (error: any) {
    yield put(actions.fetchShopsFailure(error.message || '店铺情報の取得に失敗しました'));
  }
}

// お気に入り登録
function* toggleBookmarkSaga(action: ReturnType<typeof actions.toggleBookmark>): Generator<unknown> {
  try {
    const shopId = action.payload;

    const state = (yield select()) as { shopList: ShopListState };
    const shop = state.shopList.shops.find((s) => s.id === shopId);

    if (!shop) {
      throw new Error('店舗が見つかりません');
    }

    const newBookmarkedStatus = !shop.isBookmarked;
    const memberId = sessionStorage.getItem('memberID');

    const params: any = {
      member_id: memberId,
      club_id: parseInt(shopId, 10),
      is_favorite: newBookmarkedStatus,
    };

    const response: any = yield call(reservationService().toggleFavoriteClub, params);

    if (response.data.result === 0) {
      yield put(actions.toggleBookmarkSuccess(shopId, newBookmarkedStatus));
    } else {
      throw new Error('API returned error result');
    }
  } catch (error: any) {
    yield put(actions.toggleBookmarkFailure(error.message || 'お気に入りの更新に失敗しました'));
  }
}

function* fetchCurrentLocationSaga(): Generator<unknown> {
  try {
    if (!navigator.geolocation) {
      // デフォルト設定：東京
      yield put(
        actions.setCurrentLocation({
          lat: 35.6762,
          lng: 139.6503,
        }),
      );
      return;
    }

    const position = (yield call(
      () =>
        new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            (error) => {
              reject(error);
            },
            {
              enableHighAccuracy: false,
              timeout: 5000,
              maximumAge: 0,
            },
          );
        }),
    )) as GeolocationPosition;

    yield put(
      actions.setCurrentLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }),
    );

    yield put(actions.fetchShopsRequest());
  } catch (error: any) {
    yield put(
      actions.setCurrentLocation({
        lat: 35.6762,
        lng: 139.6503,
      }),
    );
    yield put(actions.fetchShopsRequest());
  }
}

function* fetchFilterOptionsSaga(): Generator<unknown> {
  try {
    const response: any = yield call(reservationService().getClubOptions);

    if (response.data.result === 0) {
      const { region_list, tag_list } = response.data;
      const areaOptions = region_list
        .filter((region: any) => region.region_id !== 999)
        .map((region: any) => ({
          id: region.region_id.toString(),
          name: region.region_name,
        }));

      const facilities = tag_list
        .filter((tag: any) => tag.tag_manage === 1) // tag_manage = 1: 施設・設備
        .sort((a: any, b: any) => a.order_no - b.order_no)
        .map((tag: any) => ({
          id: tag.tag_id.toString(),
          name: tag.tag_name,
          icon: tag.thumbnail_image,
        }));

      const services = tag_list
        .filter((tag: any) => tag.tag_manage === 2) // tag_manage = 2: サービス
        .sort((a: any, b: any) => a.order_no - b.order_no)
        .map((tag: any) => ({
          id: tag.tag_id.toString(),
          name: tag.tag_name,
          icon: tag.thumbnail_image,
        }));

      yield put(actions.fetchFilterOptionsSuccess(facilities, services));
      yield put(actions.fetchAreaOptionsSuccess(areaOptions));
    } else {
      throw new Error('API returned error result');
    }
  } catch (error: any) {
    yield put(actions.fetchFilterOptionsFailure(error.message || '絞り込み条件の取得に失敗しました'));
    yield put(actions.fetchAreaOptionsFailure(error.message || 'エリア情報の取得に失敗しました'));
  }
}

function* fetchAreaOptionsSaga(): Generator<unknown> {
  yield;
}

export function* shopListSaga(): Generator<unknown> {
  yield takeLatest(types.FETCH_SHOPS_REQUEST, fetchShopsSaga);
  yield takeLatest(types.TOGGLE_BOOKMARK, toggleBookmarkSaga);
  yield takeLatest(types.FETCH_CURRENT_LOCATION, fetchCurrentLocationSaga);
  yield takeLatest(types.FETCH_FILTER_OPTIONS, fetchFilterOptionsSaga);
  yield takeLatest(types.FETCH_AREA_OPTIONS, fetchAreaOptionsSaga);
}

export default shopListSaga;
